import { useState, type ReactNode } from 'react';
import CpmEmptyState from '../../../components/design-system/EmptyState';
import CpmErrorCard from '../../../components/design-system/ErrorCard';
import CpmInlineErrorBanner from '../../../components/design-system/InlineErrorBanner';
import CpmToggleSwitch from '../../../components/design-system/ToggleSwitch';
import CpmTooltip from '../../../components/design-system/Tooltip';
import CpmButton from '../../../components/design-system/Button';
import type { InternalPerson } from '../../../types/accessManagement.types';
import { INTERNAL_PEOPLE_COPY } from '../internalPeopleCopy';

type PeopleSortKey = 'name' | 'phone' | 'on';

interface RowError {
    message: string;
    next: boolean;
}

interface InternalPeopleTableProps {
    rows: InternalPerson[];
    listState: 'loading' | 'ready' | 'error';
    hasPeople: boolean;
    searching: boolean;
    filter: 'on' | 'off' | null;
    searchLabel: string;
    sortKey: PeopleSortKey;
    sortDir: 'asc' | 'desc';
    onSort: (key: PeopleSortKey) => void;
    pending: Record<string, boolean>;
    rowErrors: Record<string, RowError>;
    onToggle: (person: InternalPerson, next: boolean) => void;
    onRetryRow: (person: InternalPerson) => void;
    onRetryList: () => void;
    onClearSearch: () => void;
    onClearFilter: () => void;
}

function digits(value: string): string {
    return String(value || '').replace(/\D/g, '');
}

function displayPhone(phone: string): string {
    const raw = digits(phone);
    const local = raw.length === 12 && raw.startsWith('91') ? raw.slice(2) : raw;
    if (local.length === 10) {
        return `${local.slice(0, 5)} ${local.slice(5)}`;
    }
    return local;
}

function InfoIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 16v-4" strokeLinecap="round" />
            <path d="M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

function stickyCell(width?: string) {
    return {
        position: 'sticky' as const,
        top: 64,
        zIndex: 4,
        padding: 0,
        width,
        background: '#EDEDF3',
    };
}

function SortMarks({ dir, active }: { dir: 'asc' | 'desc'; active: boolean }) {
    const up = active && dir === 'asc' ? '#6E64F0' : 'var(--cpm-text-secondary)';
    const down = active && dir === 'desc' ? '#6E64F0' : 'var(--cpm-text-secondary)';
    return (
        <svg width="9" height="13" viewBox="0 0 8 12" style={{ flex: 'none', maxWidth: 'none' }}>
            <path d="M4 0.6 L7.2 4.6 L0.8 4.6 Z" fill={up} />
            <path d="M4 11.4 L7.2 7.4 L0.8 7.4 Z" fill={down} />
        </svg>
    );
}

function PeopleColumnHead({
    label,
    active,
    dir,
    onSort,
    trailing,
}: {
    label: string;
    active: boolean;
    dir: 'asc' | 'desc';
    onSort: () => void;
    trailing?: ReactNode;
}) {
    const [hover, setHover] = useState(false);
    const color = hover ? '#6E64F0' : active ? '#0E0858' : '#1A1A1A';
    return (
        <div
            className="flex items-center"
            style={{
                gap: 6,
                padding: '10px 16px',
                background: '#EDEDF3',
                borderBottom: '1.5px solid var(--cpm-border-strong)',
                whiteSpace: 'nowrap',
            }}
        >
            <button
                type="button"
                onClick={onSort}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                className="inline-flex items-center bg-transparent border-0"
                style={{
                    gap: 5,
                    padding: '2px 0',
                    fontFamily: 'inherit',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color,
                    cursor: 'pointer',
                }}
            >
                {label}
                <SortMarks dir={dir} active={active} />
            </button>
            {trailing}
        </div>
    );
}

const SKELETON_WIDTHS = [72, 58, 84, 64, 76, 52, 80, 68];

export default function InternalPeopleTable({
    rows,
    listState,
    hasPeople,
    searching,
    filter,
    searchLabel,
    sortKey,
    sortDir,
    onSort,
    pending,
    rowErrors,
    onToggle,
    onRetryRow,
    onRetryList,
    onClearSearch,
    onClearFilter,
}: InternalPeopleTableProps) {
    const loading = listState === 'loading';
    const errored = listState === 'error';
    const ready = listState === 'ready';
    const trueEmpty = ready && !hasPeople && !searching;
    const noMatch = ready && !hasPeople && searching;
    const filterEmpty = ready && hasPeople && rows.length === 0;
    const showRows = ready && rows.length > 0;

    return (
        <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={stickyCell()}>
                            <PeopleColumnHead
                                label={INTERNAL_PEOPLE_COPY.COL_NAME}
                                active={sortKey === 'name'}
                                dir={sortDir}
                                onSort={() => onSort('name')}
                            />
                        </th>
                        <th style={stickyCell('30%')}>
                            <PeopleColumnHead
                                label={INTERNAL_PEOPLE_COPY.COL_PHONE}
                                active={sortKey === 'phone'}
                                dir={sortDir}
                                onSort={() => onSort('phone')}
                            />
                        </th>
                        <th style={stickyCell('30%')}>
                            <PeopleColumnHead
                                label={INTERNAL_PEOPLE_COPY.COL_LIST}
                                active={sortKey === 'on'}
                                dir={sortDir}
                                onSort={() => onSort('on')}
                                trailing={
                                    <CpmTooltip
                                        tone="light"
                                        fit
                                        content={INTERNAL_PEOPLE_COPY.TOOLTIP}
                                        maxWidth={280}
                                    >
                                        <span
                                            aria-label={INTERNAL_PEOPLE_COPY.TOOLTIP_LABEL}
                                            style={{
                                                display: 'inline-flex',
                                                color: 'var(--cpm-text-secondary)',
                                                cursor: 'help',
                                            }}
                                        >
                                            <InfoIcon />
                                        </span>
                                    </CpmTooltip>
                                }
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {showRows
                        ? rows.map((person) => {
                              const error = rowErrors[person._id];
                              const busy = !!pending[person._id];
                              const on = person.listOnNewBrands === true;
                              return (
                                  <tr
                                      key={person._id}
                                      onMouseEnter={(event) => {
                                          event.currentTarget.style.background =
                                              'color-mix(in oklab, #6E64F0 3%, white)';
                                      }}
                                      onMouseLeave={(event) => {
                                          event.currentTarget.style.background = '';
                                      }}
                                      style={{ borderBottom: '1px solid var(--cpm-border-default)' }}
                                  >
                                      <td
                                          style={{
                                              padding: '13px 16px',
                                              fontSize: 14,
                                              fontWeight: 800,
                                              letterSpacing: '-0.01em',
                                              color: 'var(--cpm-text-primary)',
                                              overflowWrap: 'anywhere',
                                              verticalAlign: 'middle',
                                          }}
                                      >
                                          {person.name}
                                      </td>
                                      <td
                                          style={{
                                              padding: '13px 16px',
                                              fontSize: 14,
                                              fontWeight: 800,
                                              letterSpacing: '-0.01em',
                                              fontVariantNumeric: 'tabular-nums',
                                              color: 'var(--cpm-text-primary)',
                                              verticalAlign: 'middle',
                                          }}
                                      >
                                          {displayPhone(person.phone)}
                                      </td>
                                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                                          <div className="flex flex-col" style={{ gap: 8 }}>
                                              <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                                  <CpmToggleSwitch
                                                      size="field"
                                                      checked={on}
                                                      disabled={busy}
                                                      aria-label={`List ${person.name} on new brands`}
                                                      onChange={() => onToggle(person, !on)}
                                                  />
                                                  {busy ? (
                                                      <span
                                                          style={{
                                                              fontSize: 11.5,
                                                              fontWeight: 700,
                                                              color: 'var(--cpm-text-secondary)',
                                                          }}
                                                      >
                                                          {INTERNAL_PEOPLE_COPY.SAVING}
                                                      </span>
                                                  ) : null}
                                              </div>
                                              {error ? (
                                                  <CpmInlineErrorBanner
                                                      compact
                                                      message={error.message}
                                                      onRetry={() => onRetryRow(person)}
                                                      retryLabel={INTERNAL_PEOPLE_COPY.TRY_AGAIN}
                                                  />
                                              ) : null}
                                          </div>
                                      </td>
                                  </tr>
                              );
                          })
                        : null}
                    {loading
                        ? SKELETON_WIDTHS.map((width, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid var(--cpm-border-default)' }}>
                                  <td style={{ padding: '13px 16px' }}>
                                      <span className="cpm-sk" style={{ height: 13, width: `${width}%` }} />
                                  </td>
                                  <td style={{ padding: '13px 16px' }}>
                                      <span className="cpm-sk" style={{ height: 13, width: 110 }} />
                                  </td>
                                  <td style={{ padding: '13px 16px' }}>
                                      <span className="cpm-sk" style={{ height: 22, width: 46, borderRadius: 999 }} />
                                  </td>
                              </tr>
                          ))
                        : null}
                    {trueEmpty ? (
                        <tr>
                            <td colSpan={3} style={{ padding: '44px 20px 48px' }}>
                                <div
                                    className="flex flex-col items-center text-center"
                                    style={{ gap: 6 }}
                                >
                                    <div
                                        style={{
                                            fontSize: 38,
                                            fontWeight: 800,
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: 'var(--cpm-text-primary)',
                                        }}
                                    >
                                        0
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: 14,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                            color: '#0E0858',
                                        }}
                                    >
                                        {INTERNAL_PEOPLE_COPY.EMPTY_TITLE}
                                    </div>
                                    <div
                                        style={{
                                            maxWidth: 420,
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            lineHeight: 1.45,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {INTERNAL_PEOPLE_COPY.EMPTY_BODY}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
            {errored ? (
                <div style={{ padding: 20 }}>
                    <CpmErrorCard
                        title={INTERNAL_PEOPLE_COPY.ERROR_TITLE}
                        body={INTERNAL_PEOPLE_COPY.ERROR_BODY}
                        onRetry={onRetryList}
                        retryLabel={INTERNAL_PEOPLE_COPY.RETRY}
                    />
                </div>
            ) : null}
            {noMatch ? (
                <CpmEmptyState
                    title={`${INTERNAL_PEOPLE_COPY.NO_MATCH_PREFIX} “${searchLabel}”`}
                    description={INTERNAL_PEOPLE_COPY.NO_MATCH_BODY}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onClearSearch}>
                            {INTERNAL_PEOPLE_COPY.CLEAR_SEARCH}
                        </CpmButton>
                    }
                />
            ) : null}
            {filterEmpty ? (
                <CpmEmptyState
                    title={
                        filter === 'on'
                            ? INTERNAL_PEOPLE_COPY.FILTER_EMPTY_ON
                            : INTERNAL_PEOPLE_COPY.FILTER_EMPTY_OFF
                    }
                    description={INTERNAL_PEOPLE_COPY.FILTER_EMPTY_BODY}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onClearFilter}>
                            {INTERNAL_PEOPLE_COPY.CLEAR_FILTER}
                        </CpmButton>
                    }
                />
            ) : null}
        </div>
    );
}
